export type VersionCheckTrigger = 'mount' | 'route' | 'poll' | 'visibility' | 'online'

interface DecideVersionCheckActionInput {
  trigger: VersionCheckTrigger
  fetchedVersion: string
  currentVersion: string | null
  storedVersion: string | null
  lastNotifiedVersion: string | null
}

interface VersionCheckDecision {
  forceReload: boolean
  nextCurrentVersion: string | null
  persistVersion: string | null
  notifyVersion: string | null
}

export function decideVersionCheckAction({
  trigger,
  fetchedVersion,
  currentVersion,
  lastNotifiedVersion,
}: DecideVersionCheckActionInput): VersionCheckDecision {
  if (!currentVersion) {
    return {
      forceReload: false,
      nextCurrentVersion: fetchedVersion,
      persistVersion: fetchedVersion,
      notifyVersion: null,
    }
  }

  if (fetchedVersion === currentVersion) {
    return {
      forceReload: false,
      nextCurrentVersion: currentVersion,
      persistVersion: null,
      notifyVersion: null,
    }
  }

  if (trigger === 'route') {
    return {
      forceReload: true,
      nextCurrentVersion: fetchedVersion,
      persistVersion: fetchedVersion,
      notifyVersion: null,
    }
  }

  return {
    forceReload: false,
    nextCurrentVersion: currentVersion,
    persistVersion: null,
    notifyVersion: lastNotifiedVersion === fetchedVersion ? null : fetchedVersion,
  }
}

/**
 * Classify a version-probe failure: is this the EXPECTED
 * network-unreachable case (offline-first app — airplane mode, dead
 * zones, the dev harness's offline/flaky modes) rather than a real error?
 *
 * `fetch()` signals network unreachability as a `TypeError` ("Failed to
 * fetch") — the same idiom the offline transports rely on. The version
 * probe failing offline is normal operation and must NOT be logged at
 * error level: every check tick (mount, route change, 30s poll) would
 * spam `console.error` for a state this app explicitly supports.
 * (Surfaced by the chaos lane's no-console-errors invariant during the
 * first live validation run.)
 */
export function isOfflineFetchError(err: unknown): boolean {
  return err instanceof TypeError
}

/** What the deployed `version.json` says. */
export interface DeployedBuild {
  version: string
  /**
   * Set by the build (`WEB_UPDATE_MANDATORY=true`, absent by default): a tab on another build
   * must move to this one rather than merely be told it exists (AUTHZ-02 E2, decision 3).
   */
  mandatory: boolean
}

/**
 * Parse a `version.json` body. `null` for anything without a usable version. Only a literal
 * `true` makes the update mandatory — a truthy string from a hand-edited file must not force
 * every open tab to reload.
 */
export function parseDeployedBuild(data: unknown): DeployedBuild | null {
  if (typeof data !== 'object' || data === null) return null
  const { version, mandatory } = data as { version?: unknown; mandatory?: unknown }
  if (typeof version !== 'string' || version === '') return null
  return { version, mandatory: mandatory === true }
}
